import { batch, getOwner } from "solid-js";
import { reconcile, SetStoreFunction } from "solid-js/store";
import { z } from "zod";

import useValidatedParams from "@repo/ui/signals/useValidatedParams";
import Compare from "@repo/utils/Compare";
import Range from "@repo/utils/Range";
import UArray from "@repo/utils/UArray";
import UBoolean from "@repo/utils/UBoolean";
import UPromise from "@repo/utils/UPromise";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import Cart from "~/components/checkout/Cart";

import { Bookable, useBookables } from "./bookables";
import { Booking } from "./bookings";
import { Customer, useCustomers } from "./customers";
import { Membership, useMemberships } from "./memberships";
import { Organization } from "./organizations";
import { PaymentMethod } from "./paymentMethods";
import { PaymentTerminal, usePointOfSale } from "./pointOfSale";
import { Product } from "./products";
import { Promotion, usePromotions } from "./promotions";

export namespace PosCheckout {
  export const classCost = {
    membership: {},
    credits: { credits: 1 },
    guestPasses: { guestPasses: 1 },
  } satisfies Record<string, Cart.Entry.Cost>;
}

const initialState = {
  submitAttempted: false,
  linkedCustomerId: undefined as Customer.Id | undefined,
  resourceId: undefined as PaymentMethod.Id | PaymentTerminal.Id | undefined,
  promoCodeId: undefined as Promotion.Code.Id | undefined,
  useCreditsIfAvailable: true,
  useGuestPassesIfAvailable: true,
  guestsByBookableId: {} as Record<Bookable.Id, Booking.Guest[]>,
  quantityByBookableId: {} as Record<Bookable.Id, number>,
  quantityByPlanId: {} as Record<Membership.Plan.Id, number>,
  quantityByProductId: {} as Record<Product.Id, number>,
};

export const { Context, Provider, usePosCheckout } = FeatureStore.init(
  "PosCheckout",
  initialState,
  ([store, setStore], { withLogger }) => {
    const [bookables] = useBookables();
    const [memberships] = useMemberships();
    const [customers] = useCustomers();
    const [pointOfSale, setPointOfSale] = usePointOfSale();
    const [promotions, setPromotions] = usePromotions();

    const owner = getOwner()!;

    const params = useValidatedParams(
      z.object({
        orgId: Organization.Id.zodSchema(),
      }),
    );

    const customersList = () => customers.forOrganization(params()!.orgId);

    const linkedCustomer = () =>
      customersList()?.find(
        (customer) => customer.id === accessors.linkedCustomerId(),
      );

    const newUniquePlans = () =>
      Object.keys(store.quantityByPlanId).map(
        (id) => memberships.planById(id)!,
      );

    const accessors = {
      submitAttempted: () => store.submitAttempted,
      linkedCustomerId: () => store.linkedCustomerId,
      resourceId: () => store.resourceId,
      promoCode: () => promotions.codes.byId(store.promoCodeId),
      useCreditsIfAvailable: () => store.useCreditsIfAvailable,
      useGuestPassesIfAvailable: () => store.useGuestPassesIfAvailable,
      // Credit balance after plan purchase, but before class purchase
      peakCreditBalance: () => {
        const plans = newUniquePlans();

        return (
          (linkedCustomer()?.classVoucherWallet.count || 0) +
          UArray.sum(
            plans,
            (plan) => (plan.classLimit || 0) * store.quantityByPlanId[plan.id]!,
          )
        );
      },
      peakGuestPassBalance: () => {
        const plans = newUniquePlans();

        return (
          (linkedCustomer()?.guestPassWallet.count || 0) +
          UArray.sum(
            plans,
            (plan) =>
              (plan.guestPassLimit || 0) * store.quantityByPlanId[plan.id]!,
          )
        );
      },
      cart: (): Cart => {
        const bookableEntries = Object.entries(store.quantityByBookableId)
          .map(([id, quantity]) => [bookables.byId(id)!, quantity] as const)
          .sort(Compare.comparator(([bookable]) => -bookable.priceCents));
        let availableCredits = accessors.peakCreditBalance();
        let availablePasses = accessors.peakGuestPassBalance();

        return Cart.applyPromoCode(
          [
            ...Object.entries(store.quantityByPlanId).map(
              ([itemId, quantity]) => ({
                itemId,
                quantity,
              }),
            ),
            ...bookableEntries.flatMap(([bookable, quantity]) =>
              [
                {
                  itemId: bookable.id,
                  quantity: 1,
                  cost: (() => {
                    if (
                      linkedCustomer()?.classVoucherWallet.unlimitedMembershipId
                    )
                      return {};
                    for (const plan of newUniquePlans())
                      if (plan.classLimit == null) return {};
                    const credits = +(
                      accessors.useCreditsIfAvailable() && availableCredits > 0
                    );
                    availableCredits -= credits;
                    return {
                      credits,
                      cents: (1 - credits) * bookable.priceCents,
                    };
                  })(),
                },
                quantity > 1 && {
                  label: "Guest:",
                  itemId: bookable.id,
                  quantity: quantity - 1,
                  cost: (() => {
                    if (linkedCustomer()?.guestPassWallet.unlimitedMembershipId)
                      return {};
                    for (const plan of newUniquePlans())
                      if (plan.guestPassLimit == null) return {};

                    const guestPasses = accessors.useGuestPassesIfAvailable()
                      ? Math.min(quantity - 1, availablePasses)
                      : 0;
                    availablePasses -= guestPasses;
                    return {
                      guestPasses,
                      cents: (quantity - 1 - guestPasses) * bookable.priceCents,
                    };
                  })(),
                  metadata: { guest: true },
                },
              ].filter(UBoolean.isTruthy),
            ),
            ...Object.entries(store.quantityByProductId).map(
              ([itemId, quantity]) => ({
                itemId,
                quantity,
              }),
            ),
          ],
          accessors.promoCode(),
          owner,
        );
      },
      total: () => Cart.total(accessors.cart(), owner),
      quantityByItemId: (itemId: Cart.Item.Id) => {
        if (Bookable.Id.is(itemId)) return store.quantityByBookableId[itemId];
        if (Membership.Plan.Id.is(itemId))
          return store.quantityByPlanId[itemId];
        return store.quantityByProductId[itemId];
      },
      guestsByBookableId: (bookableId: Bookable.Id) =>
        store.guestsByBookableId[bookableId],
      customerMissing: () => {
        for (const entry of accessors.cart()) {
          if (
            (Bookable.Id.is(entry.itemId) ||
              Membership.Plan.Id.is(entry.itemId)) &&
            !accessors.linkedCustomerId()
          )
            return true;
        }
        return false;
      },
      paymentMethodMissing: () =>
        accessors.total().cents > 0 && !store.resourceId,
      validateGuests: (entry: Cart.Entry) => {
        if (Bookable.Id.is(entry.itemId) && entry.metadata?.guest) {
          const guests = accessors.guestsByBookableId(entry.itemId);
          if (!guests || entry.quantity > guests.length) return false;
          for (const guest of guests.slice(0, entry.quantity))
            if (!Booking.Guest.isValid(guest)) return false;
        }
        return true;
      },
      submittable: () => {
        const cart = accessors.cart();

        if (cart.length === 0) return false;

        if (accessors.customerMissing()) return false;

        for (const entry of cart) {
          if (!accessors.validateGuests(entry)) return false;

          if (
            Cart.Item.max(Cart.Entry.resolveItem(entry, owner)) < entry.quantity
          )
            return false;
        }

        if (accessors.paymentMethodMissing()) return false;

        return true;
      },
      unusedPromoCode: () => {
        if (!store.promoCodeId) return false;
        if (
          accessors
            .cart()
            .some((entry) => entry.promoCodeId === store.promoCodeId)
        )
          return false;
        return true;
      },
    };

    const mutations = {
      submitAttempted(value: boolean) {
        setStore("submitAttempted", value);
      },
      guestsByBookableId: Object.assign(
        ((...args: any) =>
          // @ts-expect-error
          setStore("guestsByBookableId", ...args)) as SetStoreFunction<
          typeof store.guestsByBookableId
        >,
        {
          remove(bookableId: Bookable.Id, index: number) {
            setStore("guestsByBookableId", bookableId, (guests) =>
              guests.filter((_, i) => i !== index),
            );
          },
        },
      ),
      linkedCustomerId(customerId: Customer.Id | undefined) {
        setStore("linkedCustomerId", customerId);
      },
      resourceId(
        resourceId: PaymentMethod.Id | PaymentTerminal.Id | undefined,
      ) {
        setStore("resourceId", resourceId);
      },
      useCreditsIfAvailable(useCreditsIfAvailable: boolean) {
        setStore("useCreditsIfAvailable", useCreditsIfAvailable);
      },
      useGuestPassesIfAvailable(useGuestPassesIfAvailable: boolean) {
        setStore("useGuestPassesIfAvailable", useGuestPassesIfAvailable);
      },
      changeQuantityById(itemId: Cart.Item.Id, change: number) {
        const key = (() => {
          if (Bookable.Id.is(itemId)) return "quantityByBookableId";
          if (Membership.Plan.Id.is(itemId)) return "quantityByPlanId";
          return "quantityByProductId";
        })();

        setStore("submitAttempted", false);
        const newVal =
          // @ts-expect-error
          change + (store[key][itemId] ?? 0);
        if (Bookable.Id.is(itemId) && newVal > 1) {
          setStore("guestsByBookableId", itemId, (prev = []) => [
            ...prev,
            ...Range(prev.length, newVal - 1).flatMap(() => ({
              name: { first: "", last: "" },
              email: "",
            })),
          ]);
        }
        // @ts-expect-error
        setStore(key, itemId, newVal > 0 ? newVal : undefined);
      },
      clearPromoCode() {
        setStore("promoCodeId", undefined);
      },
      clearCart() {
        batch(() => {
          setStore("quantityByBookableId", reconcile({}));
          setStore("quantityByPlanId", reconcile({}));
          setStore("quantityByProductId", reconcile({}));
          setStore("guestsByBookableId", reconcile({}));
          setStore("promoCodeId", undefined);
          setStore("submitAttempted", false);
        });
      },
    };

    const actions = withLogger("action", {
      ...mutations,
      applyPromoCode(orgId: Organization.Id, code: string) {
        return setPromotions.forOrganization
          .checkPromoCode(orgId, code)
          .then((promoCode) => {
            setStore("promoCodeId", promoCode.id);
          });
      },
      async submit(
        orgId: Organization.Id,
        // eslint-disable-next-line no-unused-vars
        { onPendingTerminal = (context: { cancel(): void }) => {} } = {},
      ) {
        if (accessors.total().cents > 0 && !store.resourceId)
          throw new Error("Payment method required.");

        const cart = accessors.cart();

        return setPointOfSale
          .checkout(
            orgId,
            cart.flatMap((entry) =>
              Range(entry.quantity).map((n) => ({
                purchasableId: entry.itemId,
                resourceId: (() => {
                  // Assumes only one alternative resource is applied at a time to each cart item
                  if (entry.cost?.credits! > n)
                    return linkedCustomer()!.classVoucherWallet.id;
                  if (entry.cost?.guestPasses! > n)
                    return linkedCustomer()!.guestPassWallet.id;
                  return accessors.resourceId()!;
                })(),
                // Assumes that a promo code is only applied to entries with a quantity of 1
                // that are not purchased with an alternative resource
                promoCodeId: entry.promoCodeId,
                guestDetails:
                  entry.metadata?.guest &&
                  store.guestsByBookableId[entry.itemId as Bookable.Id]![n],
              })),
            ),
            accessors.linkedCustomerId(),
          )
          .then(async (terminalOrder) => {
            if (!terminalOrder) return;
            onPendingTerminal({
              cancel() {
                setPointOfSale.cancelTerminalOrder(terminalOrder.id);
              },
            });
            while (
              (await setPointOfSale.fetchTerminalOrder(terminalOrder.id))
                .status === "pending"
            ) {
              await UPromise.delay(1000);
            }
            return pointOfSale.terminalOrderById(terminalOrder.id);
          })
          .then((terminalOrder) => {
            // Only clear cart if all orders succeed
            if (terminalOrder?.status === "cancelled")
              throw new Error("Transaction cancelled");
            if (terminalOrder?.status === "failed")
              throw new Error("Transaction failed");

            setStore(reconcile(initialState));
            return terminalOrder;
          });
      },
    });

    return { accessors, actions };
  },
);
